import {
	Box,
	Card,
	CardContent,
	CardHeader,
	CardMedia,
	Divider,
	Skeleton,
	Stack,
	Tab,
	Tabs,
} from '@mui/material';
import { SocialInteractionBar } from 'components/pages/network/user/SocialInteractionBar/SocialInteractionBar';
import { useState } from 'react';
import { useIsMobile } from 'utils/useScreenSize';
import { useSearchParam } from 'utils/useSearchParam';
import { isDemo } from 'utils/utils';
import { ProfileHeaderEdit } from './edit';
import { useProfileHeaderQuery } from './hooks';
import { ProfileHeaderRead } from './read';
import { useAnonymousSession } from 'utils/session';
import { AnonymousSocialInteractionBar } from 'components/ui/anonymous-access/anonymous-social-interaction-bar';

/**
 * useProfileHeader() - Hook for loading editing the ProfileHeader
 *
 * @param {string} userId
 * @return {*}
 */
const useProfileHeader = (userId: string) => {
	const [isEditing, setIsEditing] = useState<boolean>(false);

	// initialFocusedField references which Formik field name will be focused when edit mode is opened,
	// for instance companyTitle when clicking the Add Title link.
	const [initialFocusedField, setIntitialFocusedField] = useState<string | null>(null);
	const { profile, loading: loadingQuery } = useProfileHeaderQuery(userId);

	const onBeginEdit = (initialFocusedField?: string) => {
		if (initialFocusedField) setIntitialFocusedField(initialFocusedField);
		setIsEditing(true);
	};

	const onCancel = () => {
		if (initialFocusedField) setIntitialFocusedField(null);
		setIsEditing(false);
	};

	return {
		profile,
		loading: loadingQuery,
		isEditing,
		onBeginEdit,
		onCancel,
		initialFocusedField,
	};
};

interface ProfileHeaderProps {
	userId: string;
}

/**
 * ProfileHeader is the top card in profile
 */
export const ProfileHeader = ({ userId }: ProfileHeaderProps) => {
	const { anonymous } = useAnonymousSession();
	const { profile, isEditing, onBeginEdit, onCancel, initialFocusedField } =
		useProfileHeader(userId);

	const [selectedTab, setSelectedTab] = useSearchParam('tab');

	const isMobile = useIsMobile();

	if (!profile) return <ProfileHeaderSkeleton />;
	return (
		<Card sx={isMobile ? { borderRadius: '0px' } : undefined}>
			{isEditing && profile ? (
				<ProfileHeaderEdit
					profile={profile}
					onCancel={onCancel}
					initialFocusedField={initialFocusedField}
				/>
			) : (
				<ProfileHeaderRead profile={profile} onBeginEdit={onBeginEdit} />
			)}
			{isMobile && isDemo() && (
				<Box p={2}>
					{anonymous ? (
						<AnonymousSocialInteractionBar />
					) : (
						<SocialInteractionBar profile={profile} />
					)}
				</Box>
			)}
			<Divider />
			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				px={{ xs: 2, sm: 2.5 }}
				spacing={3}
				overflow="auto">
				<Tabs
					sx={{ flexShrink: 0 }}
					value={selectedTab}
					onChange={(_, newValue) => setSelectedTab(newValue)}>
					<Tab label="About" value={null} />
					<Tab label="Licenses" value="licenses" />
				</Tabs>
				{!isMobile &&
					isDemo() &&
					(anonymous ? (
						<AnonymousSocialInteractionBar />
					) : (
						<SocialInteractionBar profile={profile} />
					))}
			</Stack>
		</Card>
	);
};

const ProfileHeaderSkeleton = () => (
	<Card>
		<CardMedia
			component="img"
			alt="Profile Banner"
			image="/img/profile-banner.png"
			height="160"
		/>
		<CardHeader
			style={{ height: 'inherit' }}
			avatar={<Skeleton variant="circular" width="160px" height="160px" />}
			title={<Skeleton height="2.5rem" width="10rem" />}
			subheader={
				<Stack spacing={1}>
					<Skeleton height="2rem" width="25rem" />
					<Skeleton height="2rem" width="25rem" />
					<Skeleton height="2rem" width="25rem" />
				</Stack>
			}
		/>
		<Divider />
		<CardContent>
			<Skeleton height="2rem" width="100%" />
		</CardContent>
	</Card>
);
